export type TRect = { left: number; top: number; right: number; bottom: number; width: number; height: number };
export type TViewport = { width: number; height: number };
export type TPlacement = { x: number; y: number; maxH: number };

const GAP = 8;
const EDGE = 8;
const MIN_H = 96;

// The rect the card is placed against, or null when the number is gone: a chapter re-rendered
// under an open card leaves the anchor detached, and a detached element measures as a zero rect
// at the origin, which would put the card in the top-left corner.
export const anchorRect = (anchor: Element): TRect | null => {
  if (!anchor.isConnected) return null;
  // The first client rect rather than the bounding box: a number wrapped across two lines spans
  // the column, and centring on that points the card at neither half of it.
  const rects = anchor.getClientRects();
  const rect = rects.length > 0 ? rects[0] : anchor.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return null;
  return rect;
};

export const offscreen = (r: TRect, viewport: TViewport): boolean =>
  r.bottom < 0 || r.top > viewport.height || r.right < 0 || r.left > viewport.width;

// Below the figure by preference, above it when only that side has room; when neither fits, the
// roomier side with the card capped to that room, never a card slid over the number it explains.
export const place = (anchor: TRect, card: { width: number; height: number }, viewport: TViewport): TPlacement => {
  const x = Math.max(EDGE, Math.min(anchor.left + anchor.width / 2 - card.width / 2, viewport.width - card.width - EDGE));
  const roomBelow = viewport.height - anchor.bottom - GAP - EDGE;
  const roomAbove = anchor.top - GAP - EDGE;
  if (card.height <= roomBelow) return { x: Math.round(x), y: Math.round(anchor.bottom + GAP), maxH: Math.round(roomBelow) };
  if (card.height <= roomAbove) {
    return { x: Math.round(x), y: Math.round(anchor.top - GAP - card.height), maxH: Math.round(roomAbove) };
  }
  if (roomBelow >= roomAbove) {
    const maxH = Math.max(roomBelow, MIN_H);
    return { x: Math.round(x), y: Math.round(anchor.bottom + GAP), maxH: Math.round(maxH) };
  }
  const maxH = Math.max(roomAbove, MIN_H);
  return { x: Math.round(x), y: Math.round(anchor.top - GAP - maxH), maxH: Math.round(maxH) };
};
